import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    Link,
    TextField,
    Typography,
} from '@mui/material';
import { Fragment, type JSX } from 'react';
import { getDialcodes } from '@repo/common-utils/countries';
import CountrySelect from 'src/components/common/country-select/CountrySelect';
import PhoneInput from 'src/components/common/phone-input/PhoneInput';
import { useLocale } from '@repo/i18n';
import { ContactPerson, Iso2Code, PhoneNumber } from '@repo/types';
import TextFieldStyleUtils from 'src/components/utils/jss/TextFieldStyleUtils';
import { capitalize } from '@repo/common-utils/TextUtils';
import { validateContactPerson } from '@repo/widget-utils/checkout-info-helper';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';

type Props = {
    contactPerson: ContactPerson;
    onChange: (change: Partial<ContactPerson>) => void;
    showErrors: boolean;
    showAddressFields: boolean;
};

// Static IDs and error props for form fields (singleton component - no dynamic IDs needed)
const FIELD = {
    firstName: {
        id: 'contact-firstName',
        errorProps: { id: 'contact-firstName-error', role: 'alert' as const },
    },
    lastName: {
        id: 'contact-lastName',
        errorProps: { id: 'contact-lastName-error', role: 'alert' as const },
    },
    email: {
        id: 'contact-email',
        errorProps: { id: 'contact-email-error', role: 'alert' as const },
    },
    address: {
        id: 'contact-address',
        errorProps: { id: 'contact-address-error', role: 'alert' as const },
    },
    postCode: {
        id: 'contact-postCode',
        errorProps: { id: 'contact-postCode-error', role: 'alert' as const },
    },
    city: {
        id: 'contact-city',
        errorProps: { id: 'contact-city-error', role: 'alert' as const },
    },
    privacy: {
        errorId: 'contact-privacy-error',
        invalidProps: {
            'aria-invalid': true as const,
            'aria-describedby': 'contact-privacy-error',
        },
    },
    terms: {
        errorId: 'contact-terms-error',
        invalidProps: { 'aria-invalid': true as const, 'aria-describedby': 'contact-terms-error' },
    },
} as const;

export default function ContactPersonForm(props: Props): JSX.Element {
    const { t } = useLocale();
    const config = useConfigurations();
    const { contactPerson, onChange } = props;
    const configurations = useConfigurations();

    const {
        isFirstNameValid,
        isLastNameValid,
        isPhoneDialValid,
        isPhoneNumberValid,
        isEmailValid,
        isAddressValid,
        isPostCodeValid,
        isCityValid,
    } = validateContactPerson(contactPerson, props.showAddressFields);

    const showHasAcceptedPrivacyValidationError =
        !contactPerson.hasAcceptedPrivacy && props.showErrors;
    const showHasAcceptedTermsValidationError = !contactPerson.hasAcceptedTerms && props.showErrors;
    const showFirstNameValidationError = !isFirstNameValid && props.showErrors;
    const showLastNameValidationError = !isLastNameValid && props.showErrors;
    const showPhoneDialCodeValidationError = !isPhoneDialValid && props.showErrors;
    const showPhoneNumberValidationError = !isPhoneNumberValid && props.showErrors;

    const showEmailValidationError = !isEmailValid && props.showErrors;
    const showAddressValidationError = !isAddressValid && props.showErrors;
    const showPostCodeValidationError = !isPostCodeValid && props.showErrors;
    const showCityValidationError = !isCityValid && props.showErrors;

    const errorStyles = {
        color: '#f44336',
        '& > *': {
            color: '#f44336',
        },
    };

    return (
        <Fragment>
            <FormControl>
                <InputLabel
                    htmlFor={FIELD.firstName.id}
                    error={showFirstNameValidationError}
                    required={true}
                    sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                >
                    {capitalize(t.first_name)}
                </InputLabel>
                <TextField
                    id={FIELD.firstName.id}
                    required={true}
                    error={showFirstNameValidationError}
                    aria-invalid={showFirstNameValidationError || undefined}
                    aria-describedby={
                        showFirstNameValidationError ? FIELD.firstName.errorProps.id : undefined
                    }
                    value={contactPerson.firstName}
                    onChange={(e) => {
                        onChange({ firstName: e.currentTarget.value });
                    }}
                    variant="outlined"
                    type="text"
                    color="secondary"
                    margin="dense"
                    size="small"
                    name="firstName"
                    autoComplete="given-name"
                    spellCheck={false}
                    helperText={
                        showFirstNameValidationError
                            ? `${capitalize(t.first_name)} ${t.is_required}`
                            : undefined
                    }
                    FormHelperTextProps={
                        showFirstNameValidationError ? FIELD.firstName.errorProps : undefined
                    }
                />
            </FormControl>
            <FormControl>
                <InputLabel
                    htmlFor={FIELD.lastName.id}
                    error={showLastNameValidationError}
                    required={true}
                    sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                >
                    {capitalize(t.last_name)}
                </InputLabel>
                <TextField
                    id={FIELD.lastName.id}
                    required={true}
                    error={showLastNameValidationError}
                    aria-invalid={showLastNameValidationError || undefined}
                    aria-describedby={
                        showLastNameValidationError ? FIELD.lastName.errorProps.id : undefined
                    }
                    value={contactPerson.lastName}
                    onChange={(e) => {
                        onChange({ lastName: e.currentTarget.value });
                    }}
                    variant="outlined"
                    type={'text'}
                    color="secondary"
                    margin="dense"
                    size="small"
                    name="lastName"
                    autoComplete="family-name"
                    spellCheck={false}
                    helperText={
                        showLastNameValidationError
                            ? `${capitalize(t.last_name)} ${t.is_required}`
                            : undefined
                    }
                    FormHelperTextProps={
                        showLastNameValidationError ? FIELD.lastName.errorProps : undefined
                    }
                />
            </FormControl>

            <CountrySelect
                country={contactPerson.country}
                onChange={(country: Iso2Code) => {
                    onChange({
                        country,
                        phone: { ...contactPerson.phone, dialCode: getDialcodes()[country] },
                    });
                }}
                secondaryColor={true}
            />
            {props.showAddressFields && (
                <Fragment>
                    <FormControl>
                        <InputLabel
                            error={showAddressValidationError}
                            required={true}
                            sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                        >
                            {capitalize(t.address)}
                        </InputLabel>
                        <TextField
                            error={showAddressValidationError}
                            aria-invalid={showAddressValidationError || undefined}
                            aria-describedby={
                                showAddressValidationError ? FIELD.address.errorProps.id : undefined
                            }
                            required={true}
                            value={contactPerson.address}
                            onChange={(e) => {
                                onChange({ address: e.currentTarget.value });
                            }}
                            variant="outlined"
                            type={'text'}
                            color="secondary"
                            margin="dense"
                            name="address"
                            autoComplete="street-address"
                            spellCheck={false}
                            helperText={
                                showAddressValidationError
                                    ? `${capitalize(t.address)} ${t.is_required}`
                                    : undefined
                            }
                            FormHelperTextProps={
                                showAddressValidationError ? FIELD.address.errorProps : undefined
                            }
                        />
                    </FormControl>
                    <FormControl>
                        <InputLabel
                            error={showPostCodeValidationError}
                            required={true}
                            sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                        >
                            {capitalize(t.post_code)}
                        </InputLabel>
                        <TextField
                            error={showPostCodeValidationError}
                            aria-invalid={showPostCodeValidationError || undefined}
                            aria-describedby={
                                showPostCodeValidationError
                                    ? FIELD.postCode.errorProps.id
                                    : undefined
                            }
                            required={true}
                            value={contactPerson.postCode}
                            onChange={(e) => {
                                onChange({ postCode: e.currentTarget.value });
                            }}
                            variant="outlined"
                            type={'text'}
                            color="secondary"
                            margin="dense"
                            name="postCode"
                            autoComplete="postal-code"
                            spellCheck={false}
                            helperText={
                                showPostCodeValidationError
                                    ? `${capitalize(t.post_code)} ${t.is_required}`
                                    : undefined
                            }
                            FormHelperTextProps={
                                showPostCodeValidationError ? FIELD.postCode.errorProps : undefined
                            }
                        />
                    </FormControl>
                    <FormControl>
                        <InputLabel
                            error={showCityValidationError}
                            required={true}
                            sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                        >
                            {capitalize(t.city)}
                        </InputLabel>
                        <TextField
                            error={showCityValidationError}
                            aria-invalid={showCityValidationError || undefined}
                            aria-describedby={
                                showCityValidationError ? FIELD.city.errorProps.id : undefined
                            }
                            required={true}
                            value={contactPerson.city}
                            onChange={(e) => {
                                onChange({ city: e.currentTarget.value });
                            }}
                            variant="outlined"
                            type={'text'}
                            color="secondary"
                            margin="dense"
                            name="city"
                            autoComplete="address-level2"
                            spellCheck={false}
                            helperText={
                                showCityValidationError
                                    ? `${capitalize(t.city)} ${t.is_required}`
                                    : undefined
                            }
                            FormHelperTextProps={
                                showCityValidationError ? FIELD.city.errorProps : undefined
                            }
                        />
                    </FormControl>
                </Fragment>
            )}

            <PhoneInput
                phoneNumber={contactPerson.phone}
                errorPrefix={showPhoneDialCodeValidationError}
                errorNumber={showPhoneNumberValidationError}
                onChange={(phone: PhoneNumber) => {
                    onChange({ phone });
                }}
            ></PhoneInput>

            <FormControl>
                <InputLabel
                    htmlFor={FIELD.email.id}
                    error={showEmailValidationError}
                    required={true}
                    sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
                >
                    {capitalize(t.email)}
                </InputLabel>
                <TextField
                    id={FIELD.email.id}
                    error={showEmailValidationError}
                    aria-invalid={showEmailValidationError || undefined}
                    aria-describedby={
                        showEmailValidationError ? FIELD.email.errorProps.id : undefined
                    }
                    required={true}
                    value={contactPerson.email}
                    onChange={(e) => {
                        if (e.target instanceof HTMLInputElement)
                            onChange({ email: e.target.value });
                    }}
                    variant="outlined"
                    type={'text'}
                    color="secondary"
                    margin="dense"
                    size="small"
                    name="email"
                    autoComplete="email"
                    // type="text" (not "email"), so the browser would spellcheck the address.
                    spellCheck={false}
                    helperText={
                        showEmailValidationError
                            ? `${capitalize(t.email)} ${t.is_required}`
                            : undefined
                    }
                    FormHelperTextProps={
                        showEmailValidationError ? FIELD.email.errorProps : undefined
                    }
                />
            </FormControl>
            <Box role="group" aria-label={t.consents}>
                <FormControl required={true} error={showHasAcceptedPrivacyValidationError}>
                    <FormControlLabel
                        sx={[showHasAcceptedPrivacyValidationError && errorStyles]}
                        control={
                            <Checkbox
                                required={true}
                                sx={[showHasAcceptedPrivacyValidationError && errorStyles]}
                                color="secondary"
                                checked={contactPerson.hasAcceptedPrivacy}
                                onChange={(e) => {
                                    onChange({ hasAcceptedPrivacy: e.target.checked });
                                }}
                                inputProps={
                                    showHasAcceptedPrivacyValidationError
                                        ? FIELD.privacy.invalidProps
                                        : undefined
                                }
                            />
                        }
                        labelPlacement="end"
                        label={
                            (
                                <Typography color="textSecondary" variant="body1">
                                    {capitalize(t.i_have_read_and_accepted_the)}{' '}
                                    <Link
                                        color="primary"
                                        href={configurations.privacyUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {capitalize(t.privacy_policy)}
                                    </Link>
                                </Typography>
                            ) as JSX.Element
                        }
                    />
                    {showHasAcceptedPrivacyValidationError && (
                        <FormHelperText id={FIELD.privacy.errorId} role="alert">
                            {t.must_accept_privacy_policy}
                        </FormHelperText>
                    )}
                </FormControl>

                <FormControl required={true} error={showHasAcceptedTermsValidationError}>
                    <FormControlLabel
                        sx={[showHasAcceptedTermsValidationError && errorStyles, { mt: 0 }]}
                        control={
                            <Checkbox
                                sx={[showHasAcceptedTermsValidationError && errorStyles]}
                                checked={contactPerson.hasAcceptedTerms}
                                color="secondary"
                                onChange={(e) => {
                                    onChange({ hasAcceptedTerms: e.target.checked });
                                }}
                                inputProps={
                                    showHasAcceptedTermsValidationError
                                        ? FIELD.terms.invalidProps
                                        : undefined
                                }
                            />
                        }
                        labelPlacement="end"
                        label={
                            (
                                <Typography color="textSecondary" variant="body1">
                                    {capitalize(t.i_have_read_and_accepted_the)}{' '}
                                    <Link
                                        color="primary"
                                        href={configurations.termsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {capitalize(t.terms_and_conditions)}
                                    </Link>
                                </Typography>
                            ) as JSX.Element
                        }
                        color="textSecondary"
                    />
                    {showHasAcceptedTermsValidationError && (
                        <FormHelperText id={FIELD.terms.errorId} role="alert">
                            {t.must_accept_terms_and_conditions}
                        </FormHelperText>
                    )}
                </FormControl>
                {config.enableNewsletter && (
                    <FormControl required={false}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={contactPerson.receiveNewsletter}
                                    color="secondary"
                                    onChange={(e) => {
                                        onChange({ receiveNewsletter: e.target.checked });
                                    }}
                                />
                            }
                            labelPlacement="end"
                            label={
                                (
                                    <Typography color="textSecondary" variant="body1">
                                        {capitalize(t.iWishToRecieveNewsletters)}
                                    </Typography>
                                ) as JSX.Element
                            }
                            color="textSecondary"
                        />
                    </FormControl>
                )}
            </Box>
        </Fragment>
    );
}
